import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Container } from 'react-bootstrap';
import { getLoggerTableColumnConfig } from './helpers';
import ErrorView from 'sharedComponents/ErrorBoundary/ErrorView';
import { apiCallStatus } from 'helpers/constants';
import { useReportIssue } from 'sharedHooks/useReportIssue';

import { GeneralTable, LoadingStatusWrapper } from 'sharedComponents';

import './style.scss';

const LoggerDownloadComponent = ({ model, actions }) => {
    const { t: translate } = useTranslation();
    const { loggerId, loggers } = model;
    const { getLoggers, getLoggerId, clearLoggerFile } = actions;
    const { showReportIssue } = useReportIssue();
    const selectedEvent = useSelector(({ eventLog }) => {
        return eventLog?.selectedEvent;
    });

    useEffect(() => {
        if (loggerId && loggerId.length) {
            getLoggers(loggerId);
        }
    }, [loggerId, getLoggers]);

    if (model.errorData?.loadingStatus === apiCallStatus.ERROR) {
        const hasLoggerIds = Array.isArray(loggerId) && loggerId.length > 0;
        const onRetry = model.loggersLoadingStatus === apiCallStatus.SUCCESS
            ? () => clearLoggerFile()
            : () => hasLoggerIds ? getLoggers(loggerId) : getLoggerId(selectedEvent?.rawEvent);
        return <ErrorView
            errorCode={model.errorData.errorCode}
            onRetry={onRetry}
            onReportIssue={showReportIssue}
        />;
    }

    const hasLoggers = selectedEvent.info.hasDataLoggers || selectedEvent.info.hasBinaryLogger;

    const tableData = loggers?.length ? loggers : loggerId?.length && hasLoggers ? loggerId : [];

    return <Container fluid className='logger-download-container'>
        <LoadingStatusWrapper loadingStatus={[model?.loggersLoadingStatus, model?.loggerFileLoadingStatus]}>
            <GeneralTable
                scroll={{ x: 720, y: null }}
                tableKey='id'
                columns={getLoggerTableColumnConfig(translate, actions?.downloadLogger, tableData, selectedEvent?.timestamp, model?.dateKind, ...loggerId)}
                data={tableData}
                emptyText={translate('ABB.Powertrain.Frontend.noInformationAvailableLabel')}
                paginationProps={{ pageSize: 12 }}
            />
        </LoadingStatusWrapper>
    </Container>;
};

LoggerDownloadComponent.propTypes = {
    selectedAsset: PropTypes.shape({
        id: PropTypes.string, // Asset ID
        name: PropTypes.string, // Asset name
    }), // Selected asset details
    model: PropTypes.shape({
        loggerId: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.string, // Logger ID
                type: PropTypes.string, // Logger type (e.g., DATA_LOGGER)
            })
        ), // Array of logger IDs
        loggers: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.string, // Logger ID
                name: PropTypes.string, // Logger name
                type: PropTypes.string, // Logger type
            })
        ), // Array of loggers
        dateKind: PropTypes.number, // Date kind for logger data
        loggersLoadingStatus: PropTypes.string, // Loading status for loggers
        loggerFileLoadingStatus: PropTypes.string, // Loading status for logger files
        errorData: PropTypes.shape({
            loadingStatus: PropTypes.string,
            errorCode: PropTypes.number,
        }),
    }), // Model containing logger data
    actions: PropTypes.shape({
        getLoggers: PropTypes.func, // Function to fetch loggers
        getLoggerId: PropTypes.func,
        clearLoggerFile: PropTypes.func,
        downloadLogger: PropTypes.func, // Function to download logger files
    }), // Actions object containing functions
};

export default LoggerDownloadComponent;
